import { useEffect, useRef, useState } from 'react';
import { Input } from '$lib/components/ui/Input';

export interface AutocompleteItem {
  label: string;
  street_name: string;
  house_number: string;
  postal_code: string;
  city: string | null;
  floor: string | null;
  door: string | null;
}

export interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the operator picks a suggestion. Includes floor/door from DAR. */
  onSelect?: (item: AutocompleteItem) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  testId?: string;
  autoFocus?: boolean;
}

const DEBOUNCE_MS = 200;
const MIN_QUERY_LEN = 2;

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  required,
  minLength = 2,
  testId = 'estimate-address-input',
  autoFocus
}: AddressAutocompleteProps) {
  const [items, setItems] = useState<AutocompleteItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
    }
    if (value.trim().length < MIN_QUERY_LEN) {
      setItems([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const url = `/api/v1/dar/autocomplete?q=${encodeURIComponent(value)}&limit=10`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          setItems([]);
          return;
        }
        const data = (await res.json()) as { items: AutocompleteItem[] };
        setItems(data.items);
        setIsOpen(data.items.length > 0);
        setHighlight(0);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pick = (item: AutocompleteItem) => {
    onChange(item.label);
    setIsOpen(false);
    onSelect?.(item);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(items[highlight]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid="address-autocomplete">
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => items.length > 0 && setIsOpen(true)}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        data-testid={testId}
        autoFocus={autoFocus}
        autoComplete="off"
      />
      {loading && (
        <span
          className="absolute right-2 top-1/2 -translate-y-1/2 text-2xs text-fg-subtle"
          aria-hidden
        >
          …
        </span>
      )}
      {isOpen && items.length > 0 && (
        <ul
          role="listbox"
          data-testid="address-autocomplete-list"
          className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-sm border border-rule bg-surface shadow-lg"
        >
          {items.map((item, i) => (
            <li
              key={`${item.label}|${i}`}
              role="option"
              aria-selected={i === highlight}
              data-testid={`address-autocomplete-item-${i}`}
              className={
                'flex cursor-pointer items-center justify-between px-3 py-1.5 text-xs hover:bg-surface-2 ' +
                (i === highlight ? 'bg-surface-2' : '')
              }
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span className="truncate text-fg">{item.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
